/**
 * Filo panelinde kimin neyi görebileceğinin TEK kaynağı.
 *
 * Bit değerleri backend'deki FleetSupervisorPermission ile birebir aynıdır; maske sayı
 * olarak taşınır. Sahip (isOwner) maskeye HİÇ bakılmadan geçer — sahibin yetkisi
 * veriden değil, kim olduğundan gelir.
 */
export const FLEET_PERMISSION = {
    DeliveryView: 1,
    DeliveryAssign: 2,
    CourierView: 4,
    CourierManage: 8,
    JoinRequestManage: 16,
    CourierCostView: 32,
    CourierCostManage: 64,
    PayoutView: 128,
    PayoutApprove: 256,
    TariffView: 512,
    TariffManage: 1024,
    ReportView: 2048,
    LiveMapView: 4096,
    HeatmapView: 8192,
    // Anasayfa ayrı bir bit: kontrol paneli filo kazancı, kurye ödemesi ve kazanç
    // grafiklerini taşıyor, teslimat yetkisiyle birlikte açılmamalı.
    DashboardView: 16384,
};

/**
 * Menü adlarıyla gruplanmış yetki etiketleri. Sıra Sidebar'daki grup sırasını izler.
 * Hem sorumlu ekleme modalı hem sorumlunun kendi hesap ekranı bunu kullanır —
 * iki yerde ayrı etiket listesi tutmak, biri güncellenince diğerini yalancı yapardı.
 */
export const PERMISSION_GROUPS = [
    {
        label: 'Genel',
        keys: [
            ['DashboardView', 'Anasayfayı görüntüle'],
        ],
    },
    {
        label: 'Teslimatlar',
        keys: [
            ['DeliveryView', 'Teslimatları görüntüle'],
            ['DeliveryAssign', 'Kurye ata'],
            ['LiveMapView', 'Canlı haritayı görüntüle'],
            ['HeatmapView', 'Isı haritasını görüntüle'],
        ],
    },
    {
        label: 'Kuryeler',
        keys: [
            ['CourierView', 'Kuryeleri görüntüle'],
            ['CourierManage', 'Kuryeleri yönet'],
            ['JoinRequestManage', 'Katılma taleplerini yönet'],
            ['CourierCostView', 'Kurye ücretlerini görüntüle'],
            ['CourierCostManage', 'Kurye ücretlerini yönet'],
        ],
    },
    {
        label: 'Kazanç',
        keys: [
            ['PayoutView', 'Hakedişleri görüntüle'],
            ['PayoutApprove', 'Hakedişleri onayla'],
            ['TariffView', 'Filo tarifesini görüntüle'],
            ['TariffManage', 'Filo tarifesini yönet'],
        ],
    },
    {
        label: 'Raporlar',
        keys: [
            ['ReportView', 'Raporları görüntüle'],
        ],
    },
];

/** Sayfa → açmak için gereken yetki. Listede olmayan sayfa herkese açıktır. */
export const PAGE_PERMISSION = {
    dashboard: FLEET_PERMISSION.DashboardView,
    deliveries: FLEET_PERMISSION.DeliveryView,
    'live-map': FLEET_PERMISSION.LiveMapView,
    heatmap: FLEET_PERMISSION.HeatmapView,
    members: FLEET_PERMISSION.CourierView,
    'join-requests': FLEET_PERMISSION.JoinRequestManage,
    'courier-costs': FLEET_PERMISSION.CourierCostView,
    payouts: FLEET_PERMISSION.PayoutView,
    earnings: FLEET_PERMISSION.PayoutView,
    'workday-earnings': FLEET_PERMISSION.PayoutView,
    'cost-history': FLEET_PERMISSION.TariffView,
    reports: FLEET_PERMISSION.ReportView,
};

/** Yalnız filo sahibine açık sayfalar (filo geneli finans/hukuk + sorumlu yönetimi). */
export const OWNER_ONLY_PAGES = [
    'supervisors', 'fleet-payments', 'invoices', 'platform-licences',
    'package-bonus', 'stacking-fee', 'legal-agreements', 'fleet-readiness',
];

export function can(ctx, flag) {
    if (!ctx) return false;
    if (ctx.isOwner) return true;
    return (Number(ctx.permissions || 0) & flag) === flag;
}

export function canAny(ctx, flags) {
    return flags.some(f => can(ctx, f));
}

/**
 * Menü sırasına göre açılabilen İLK sayfa. Anasayfa artık yetkiye bağlı olduğu için
 * giriş sonrası ya da "erişiminiz yok" ekranından dönüşte körlemesine dashboard'a
 * gitmek, yetkisi olmayan sorumluyu ret ekranında kilitliyordu.
 * En sonda `profile` var: her role açık ve sorumlu için kendi hesap ekranı.
 */
export const PAGE_ORDER = [
    'dashboard', 'deliveries', 'live-map', 'heatmap',
    'members', 'join-requests', 'courier-costs',
    'payouts', 'earnings', 'cost-history', 'reports',
    'announcements', 'profile',
];

export function firstAccessiblePage(ctx) {
    return PAGE_ORDER.find(p => canOpenPage(p, ctx)) || 'profile';
}

export function canOpenPage(pageId, ctx) {
    if (!ctx) return false;
    if (OWNER_ONLY_PAGES.includes(pageId)) return !!ctx.isOwner;

    const required = PAGE_PERMISSION[pageId];
    if (!required) return true;

    return can(ctx, required);
}

/** Menü gruplarını süzer; hiç maddesi kalmayan grubu düşürür. */
export function visibleNavGroups(groups, ctx) {
    return groups
        .map(g => ({ ...g, items: g.items.filter(i => canOpenPage(i.id, ctx)) }))
        .filter(g => g.items.length > 0);
}
